// Centralised routing helper for the SpaceCost product.
//
// SpaceCost is built and hosted as a separate Emergent project (its own
// frontend, backend and database) so that FlipCost stays focused on
// whole-property flips. While SpaceCost is being built we still ship a
// lightweight teaser page at the internal `/spacecost` route so links from
// MyPlanCost don't 404.
//
// Once SpaceCost is deployed, set REACT_APP_SPACECOST_URL in
// `frontend/.env` (e.g. https://spacecost.myplancost.com) and every link
// from FlipCost will start opening the real product instead of the teaser.

const RAW_URL = (process.env.REACT_APP_SPACECOST_URL || "").trim();

/**
 * Returns the absolute URL for the live SpaceCost app, or null if the
 * env var hasn't been set yet (in which case callers should fall back to
 * the internal teaser route).
 */
export function getSpaceCostUrl() {
  if (!RAW_URL) return null;
  // Allow callers to set the value with or without a trailing slash.
  return RAW_URL.replace(/\/+$/, "");
}

/**
 * Navigate to SpaceCost from anywhere in the FlipCost app.
 *
 * @param {Function} navigate react-router-dom navigate function (used as
 *   the fallback when REACT_APP_SPACECOST_URL is not configured).
 * @param {Object}   [opts]
 * @param {string}   [opts.path]    Optional path to append to the live
 *   SpaceCost URL (e.g. "/pricing"). Ignored when falling back to the
 *   internal teaser page.
 * @param {boolean}  [opts.newTab=false] Open the live SpaceCost app in a
 *   new tab when true.
 */
export function goToSpaceCost(navigate, opts = {}) {
  const base = getSpaceCostUrl();
  if (!base) {
    // SpaceCost not deployed yet — show the internal teaser page.
    navigate("/spacecost");
    return;
  }
  const path = opts.path ? (opts.path.startsWith("/") ? opts.path : `/${opts.path}`) : "";
  const url = `${base}${path}`;
  if (opts.newTab) {
    window.open(url, "_blank", "noopener,noreferrer");
  } else {
    window.location.href = url;
  }
}

/**
 * True when SpaceCost is live (env var set). Useful for swapping
 * "Coming soon" badges for "Open SpaceCost" CTAs.
 */
export function isSpaceCostLive() {
  return Boolean(getSpaceCostUrl());
}
